import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Reveal } from "./Reveal";

const STEPS = [
  {
    tag: "RIGHT NOW",
    n: "01",
    title: "Get the water out and drying started",
    body: "Every hour standing water sits, it travels further — into drywall, subfloor, and framing. The first move is always extraction and drying, not paperwork.",
  },
  {
    tag: "WITHIN THE HOUR",
    n: "02",
    title: "Tell us what happened",
    body: "A short intake and a few photos. Those photos aren't an estimate — they're so our crew arrives with the right equipment for your specific loss.",
  },
  {
    tag: "SAME DAY",
    n: "03",
    title: "A crew is dispatched",
    body: "We extract standing water, set professional drying equipment, and begin documenting the source and the damage from the moment we arrive.",
  },
  {
    tag: "IN PARALLEL",
    n: "04",
    title: "You open your insurance claim",
    body: "The claim runs alongside the work — it does not gate it. Coverage questions belong with your declarations page and your broker, and they can be answered while your home dries.",
  },
  {
    tag: "OVER THE COMING DAYS",
    n: "05",
    title: "We monitor until it's dry",
    body: "We track moisture readings until the structure is fully dry and the whole process is documented — a clear, dated record of prompt action.",
  },
];

const Step = ({ s, reduce }) => (
  <li className="relative pl-8 sm:pl-12 pb-12 last:pb-0">
    <motion.span
      className="absolute -left-[7px] top-1.5 h-3.5 w-3.5 rounded-full bg-erg-aqua ring-4 ring-erg-mid/40"
      initial={reduce ? undefined : { scale: 0.4, opacity: 0 }}
      whileInView={reduce ? undefined : { scale: 1, opacity: 1 }}
      viewport={{ once: true, margin: "-45% 0px -45% 0px" }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    />
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
      <motion.span
        className="font-mono text-4xl sm:text-5xl text-erg-aqua/90 tabular-nums leading-none"
        initial={reduce ? undefined : { scale: 0.82, opacity: 0.3 }}
        whileInView={reduce ? undefined : { scale: [0.82, 1.08, 1], opacity: 1 }}
        viewport={{ once: true, margin: "-45% 0px -45% 0px" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        {s.n}
      </motion.span>
      <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-erg-dry/50">
        {s.tag}
      </span>
    </div>
    <h3 className="mt-3 font-display font-semibold text-erg-dry text-2xl sm:text-3xl leading-tight">
      {s.title}
    </h3>
    <p className="mt-2 text-erg-dry/75 text-base sm:text-lg leading-relaxed max-w-2xl">
      {s.body}
    </p>
  </li>
);

export const FirstHours = () => {
  const reduce = useReducedMotion();
  const listRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: ["start 75%", "end 55%"],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section data-testid="first-hours-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <span className="font-mono text-[12px] tracking-[0.22em] uppercase text-erg-aqua/80">
              The real process
            </span>
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="mt-4 font-display font-bold uppercase text-erg-dry leading-[0.95] text-[clamp(2rem,6vw,3.75rem)]">
              The first 24 hours
            </h2>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="mt-5 text-erg-dry/75 text-lg leading-relaxed">
              No mystery, no scramble. This is the plain sequence — the same one we run every day.
            </p>
          </Reveal>
        </div>

        <ol ref={listRef} className="mt-14 relative ml-1 sm:ml-3">
          {/* Faint track + scroll-driven "clock advancing" fill */}
          <div className="absolute left-0 top-0 bottom-0 w-px bg-erg-aqua/15" aria-hidden="true" />
          <motion.div
            aria-hidden="true"
            className="absolute left-0 top-0 bottom-0 w-px origin-top bg-erg-aqua"
            style={{ scaleY: reduce ? 1 : lineScale }}
          />
          <div className="pl-0">
            {STEPS.map((s) => (
              <Step key={s.n} s={s} reduce={reduce} />
            ))}
          </div>
        </ol>
      </div>
    </section>
  );
};
